import { Link } from "react-router-dom";
import {
  Siren, MessageCircleQuestion, SearchCheck, FileText, CalendarClock,
  Users, RotateCcw, PhoneCall, ArrowRight, Phone, Scale,
} from "lucide-react";
import { SITUATIONS } from "@/data/situations";
import { FIRM } from "@/data/content";
import { PageHero, Breadcrumbs, TestimonialBand, FadeIn } from "@/components/Shared";

const ICONS = { Siren, MessageCircleQuestion, SearchCheck, FileText, CalendarClock, Users, RotateCcw, PhoneCall };

export default function HowWeHelp() {
  return (
    <main data-testid="how-we-help-page">
      <PageHero
        eyebrow="Route 2 — How We Help"
        title="Tell us your situation."
        lead="You don't need to know what the law calls your problem. Start with what is happening to you — each page below reassures you on the immediate steps, then points you to the detailed advice you need."
      >
        <a
          href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
          className="mt-8 inline-flex items-center gap-3 text-lg text-white transition-colors hover:text-brand-gold"
          data-testid="how-we-help-emergency"
        >
          <span className="flex size-10 items-center justify-center bg-white/10">
            <Phone className="size-4 text-brand-terracotta" />
          </span>
          <span>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Need help right now? 24hr line
            </span>
            {FIRM.emergencyPhone}
          </span>
        </a>
      </PageHero>
      <Breadcrumbs items={[{ label: "How We Help" }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SITUATIONS.map((s, i) => {
              const Icon = ICONS[s.icon] || Scale;
              return (
                <FadeIn key={s.slug} delay={(i % 4) * 0.07} className="h-full">
                  <Link
                    to={`/how-we-help/${s.slug}`}
                    className="brand-card group flex h-full flex-col p-7"
                    data-testid={`situation-card-${s.slug}`}
                  >
                    <Icon className={`size-6 ${s.urgent ? "text-brand-terracotta" : "text-brand-gold"}`} />
                    <h3 className="mt-6 flex-1 text-lg font-medium leading-snug text-brand-navy">
                      {s.title}
                    </h3>
                    <span className="brand-text-link mt-5 flex items-center gap-2">
                      {s.urgent ? "Urgent guidance" : "What to do now"}
                      <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-slate-500">
              Prefer to browse by offence instead?{" "}
              <Link to="/services" className="font-semibold text-brand-terracotta transition-colors hover:text-brand-terracotta-dark" data-testid="how-we-help-to-services">
                View our services by legal category
              </Link>
              .
            </p>
          </FadeIn>
        </div>
      </section>

      <TestimonialBand />
    </main>
  );
}
